import { SidePanelDisplayStyle } from 'designSystem/components/sidePanelContext'
import { type MutableRefObject, type RefObject, useEffect } from 'react'

export function useSidePanelDismiss({
  asideRef,
  closeOnEscape,
  closeOnOutsideClick,
  modal,
  onClose,
  open,
  panelLayoutRef,
  skipNextOutsideClickRef,
}: {
  asideRef: RefObject<HTMLElement | null>
  closeOnEscape: boolean
  closeOnOutsideClick: boolean
  modal: boolean
  onClose: () => void
  open: boolean
  panelLayoutRef: RefObject<HTMLDivElement | null>
  skipNextOutsideClickRef: MutableRefObject<boolean>
}): void {
  useEffect(() => {
    if (!open || modal || !closeOnOutsideClick) return

    const onDocClick = (event: MouseEvent) => {
      if (skipNextOutsideClickRef.current) {
        skipNextOutsideClickRef.current = false
        return
      }
      const panelLayout = panelLayoutRef.current
      if (!panelLayout) return
      if (panelLayout.dataset.panelDisplayStyle !== SidePanelDisplayStyle.Sheet)
        return

      const target = event.target
      if (!(target instanceof Element)) return
      if (!target.isConnected) return

      const aside = asideRef.current
      if (aside?.contains(target)) return

      onClose()
    }

    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [
    asideRef,
    closeOnOutsideClick,
    modal,
    onClose,
    open,
    panelLayoutRef,
    skipNextOutsideClickRef,
  ])

  useEffect(() => {
    if (!open || modal || !closeOnEscape) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const panelLayout = panelLayoutRef.current
      if (!panelLayout) return
      if (panelLayout.dataset.panelDisplayStyle !== SidePanelDisplayStyle.Sheet)
        return
      onClose()
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeOnEscape, modal, onClose, open, panelLayoutRef])
}
